import { useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Card, Dropdown, Flex, Input, Modal, Select, Table, Tabs, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useAtomValue } from 'jotai';
import { ReactComponent as Search } from '../../../assets/magnifier.svg';
import { ReactComponent as Ellipsis } from '../../../assets/ellipsis.svg';
import { usePagination } from '../../../hooks/usePagination';

import {
  admitApplicants,
  batchRejectApplicants,
  batchResetRejection,
  changeCourse,
  downloadStudentUsers,
  getAllStudentUser,
  bulkApproveAdmission,
  bulkRejectAdmission,
  getPendingApprovals,
} from '../request';
import { useGetSpecializationQuery } from '../../../hooks/api/specialization/useGetSpecializationQuery';
import {
  getAllAcademicSession,
  getAllApplicationBatch,
  getAllModeOfStudy,
  getAllPrograms,
  getAllProgramType,
} from '../../../requests/index';
import { getAllProgram } from '../../setup/program/request';
import styles from '../styles.module.scss';
import { useDebounce } from '../../../hooks/useDebounce';
import { useNavigate } from 'react-router-dom';
import Button from '../../../custom/button/button';
import { useDownload } from '../../../hooks/useDownload';
import TextInput from '../../../custom/input/input';
import { useResetRejectionMutation } from '../../../hooks/api/useResetRejectionMutation';
import { useGetSchoolConfigQuery } from '../../../hooks/api/schoolConfig/useGetSchoolConfigQuery';
import { getAdmissionStages, AdmissionStage } from '../../setup/admissionApproval/request';
import { userAtom } from '../../../utils/store';

// ── Pending Approval Tab ──────────────────────────────────────────────────────
// Self-contained per-stage component so each stage has fully independent state.

const pendingColumns: ColumnsType<User> = [
  { key: 'appNo', title: 'Application No', dataIndex: 'applicationNumber' },
  { key: 'firstName', title: 'First Name', dataIndex: 'firstName' },
  { key: 'lastName', title: 'Last Name', dataIndex: 'lastName' },
  { key: 'programName', title: 'Program Name', dataIndex: 'programName' },
  { key: 'programTypeName', title: 'Program Type', dataIndex: 'programTypeName' },
  { key: 'applicationBatchName', title: 'Application Batch', dataIndex: 'applicationBatchName' },
  { key: 'email', title: 'Email', dataIndex: 'email' },
];

interface PendingApprovalTabProps {
  stage: AdmissionStage;
}

const PendingApprovalTab = ({ stage }: PendingApprovalTabProps) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const [selected, setSelected] = useState<React.Key[]>([]);
  const [page, setPage] = useState(1);
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);

  const { data: pendingData, isLoading } = useQuery({
    queryKey: ['pending-approvals', stage.stageOrder, page],
    queryFn: () => getPendingApprovals({ stageOrder: stage.stageOrder, page }),
  });

  const approveMutation = useMutation({ mutationFn: bulkApproveAdmission });
  const rejectMutation = useMutation({ mutationFn: bulkRejectAdmission });

  const items = (pendingData?.data ?? []) as User[];
  const total = pendingData?.totalRecords ?? 0;

  const handleApprove = async (notes: string, resetForm: () => void) => {
    try {
      await approveMutation.mutateAsync(
        { applicationNumbers: selected as string[], stageOrder: stage.stageOrder, notes },
        {
          onSuccess: (res) => {
            notification.success({ message: 'Success', description: res?.message || `${selected.length} applicant(s) approved` });
            queryClient.invalidateQueries({ queryKey: ['pending-approvals', stage.stageOrder] });
            setSelected([]);
            setIsApproveOpen(false);
            resetForm();
          },
        },
      );
    } catch (err: any) {
      notification.error({ message: 'Error', description: err?.response?.data?.message || 'Failed to approve' });
    }
  };

  const handleReject = async (notes: string, resetForm: () => void) => {
    try {
      await rejectMutation.mutateAsync(
        { applicationNumbers: selected as string[], stageOrder: stage.stageOrder, notes },
        {
          onSuccess: (res) => {
            notification.success({ message: 'Success', description: res?.message || `${selected.length} applicant(s) rejected` });
            queryClient.invalidateQueries({ queryKey: ['pending-approvals', stage.stageOrder] });
            setSelected([]);
            setIsRejectOpen(false);
            resetForm();
          },
        },
      );
    } catch (err: any) {
      notification.error({ message: 'Error', description: err?.response?.data?.message || 'Failed to reject' });
    }
  };

  return (
    <Card bordered={false}>
      <p style={{ marginBottom: 12, color: '#667085', fontSize: 13 }}>
        Pending approvals for <strong>Stage {stage.stageOrder} — {stage.stageName}</strong>.
        {stage.isFinalStage && <Tag color="green" style={{ marginLeft: 8 }}>Final Stage</Tag>}
      </p>

      {selected.length > 0 && (
        <Flex gap="8px" style={{ marginBottom: 12 }}>
          <Button
            text={`${stage.actionLabel} (${selected.length})`}
            onClick={() => setIsApproveOpen(true)}
            disabled={approveMutation.isPending || rejectMutation.isPending}
          />
          <Button
            text={`${stage.rejectLabel} (${selected.length})`}
            onClick={() => setIsRejectOpen(true)}
            disabled={approveMutation.isPending || rejectMutation.isPending}
            style={{ backgroundColor: '#dc2626', color: '#fff' }}
          />
        </Flex>
      )}

      <Table
        dataSource={items}
        columns={pendingColumns}
        rowKey={(r) => r.applicationNumber}
        rowSelection={{ selectedRowKeys: selected, onChange: setSelected }}
        scroll={{ x: true }}
        loading={isLoading}
        locale={{
          emptyText: (
            <div style={{ padding: '24px 0', color: '#667085', textAlign: 'center' }}>
              <p style={{ margin: '0 0 4px', fontWeight: 500 }}>No pending approvals at this stage</p>
              <p style={{ margin: 0, fontSize: 12 }}>
                Applicants submitted through the approval workflow will appear here.
              </p>
            </div>
          ),
        }}
        pagination={{
          position: ['bottomCenter'],
          current: page,
          total,
          onChange: setPage,
          pageSize: 10,
        }}
      />

      {/* Approve modal */}
      <Modal
        open={isApproveOpen}
        onCancel={() => setIsApproveOpen(false)}
        footer={null}
        destroyOnClose
        title={`${stage.actionLabel} — ${selected.length} applicant(s)`}
      >
        <Formik
          initialValues={{ notes: '' }}
          validationSchema={Yup.object().shape({ notes: Yup.string().trim().required('Notes are required') })}
          onSubmit={async (values, { resetForm }) => handleApprove(values.notes.trim(), resetForm)}
        >
          <Form className={styles.verifyForm}>
            <TextInput name="notes" label="Notes" placeholder="Enter approval notes" type="textarea" />
            <Flex justify="flex-end" gap="12px" className={styles.verifyActions}>
              <Button type="button" variant="text" text="Cancel" disabled={approveMutation.isPending} onClick={() => setIsApproveOpen(false)} />
              <Button type="submit" text={approveMutation.isPending ? 'Approving...' : 'Approve'} isLoading={approveMutation.isPending} disabled={approveMutation.isPending} />
            </Flex>
          </Form>
        </Formik>
      </Modal>

      {/* Reject modal */}
      <Modal
        open={isRejectOpen}
        onCancel={() => setIsRejectOpen(false)}
        footer={null}
        destroyOnClose
        title={`${stage.rejectLabel} — ${selected.length} applicant(s)`}
      >
        <Formik
          initialValues={{ notes: '' }}
          validationSchema={Yup.object().shape({ notes: Yup.string().trim().required('Notes are required') })}
          onSubmit={async (values, { resetForm }) => handleReject(values.notes.trim(), resetForm)}
        >
          <Form className={styles.verifyForm}>
            <TextInput name="notes" label="Notes" placeholder="Enter rejection notes" type="textarea" />
            <Flex justify="flex-end" gap="12px" className={styles.verifyActions}>
              <Button type="button" variant="text" text="Cancel" disabled={rejectMutation.isPending} onClick={() => setIsRejectOpen(false)} />
              <Button type="submit" text={rejectMutation.isPending ? 'Rejecting...' : 'Reject'} isLoading={rejectMutation.isPending} disabled={rejectMutation.isPending} style={{ backgroundColor: '#dc2626' }} />
            </Flex>
          </Form>
        </Formik>
      </Modal>
    </Card>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const StudentUser = () => {
  const { notification } = App.useApp();
  const { currentPage, onChange } = usePagination();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAtomValue(userAtom);
  const userRoles = user?.role ?? [];

  const [filters, setFilters] = useState<ApplicantParams>({
    programId: 0, modeOfStudyId: 0, programTypeId: 0,
    applicationBatchId: 0, sessionId: 0, name: '',
  });
  const [appNumberSearch, setAppNumberSearch] = useState('');
  const [selectedApplicants, setSelectedApplicants] = useState<React.Key[]>([]);
  const [selectedResetApplicant, setSelectedResetApplicant] = useState<User | null>(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isBulkUndoModalOpen, setIsBulkUndoModalOpen] = useState(false);
  const [changeCourseApplicant, setChangeCourseApplicant] = useState<User | null>(null);
  const debouncedName = useDebounce(filters.name || '', 500);
  const debouncedAppNumber = useDebounce(appNumberSearch, 500);
  const { downloadExcelFile } = useDownload();
  const { resetRejectionHandler, isResetRejectionLoading } = useResetRejectionMutation(
    selectedResetApplicant?.applicantId,
    () => setSelectedResetApplicant(null),
  );

  const downloadApplicantsMutation = useMutation({ mutationFn: downloadStudentUsers });
  const admitApplicantsMutation = useMutation({ mutationFn: admitApplicants });
  const rejectApplicantMutation = useMutation({ mutationFn: batchRejectApplicants });
  const bulkUndoMutation = useMutation({ mutationFn: batchResetRejection });
  const changeCourseMutation = useMutation({ mutationFn: changeCourse });

  // The backend currently ignores/mishandles these dropdown filters, so once any of
  // them is set we pull a much larger batch and filter+paginate on the client as a
  // safety net (see filteredData below), instead of trusting the server-side filter.
  const hasActiveSelectFilter = Boolean(
    filters.programId || filters.modeOfStudyId || filters.programTypeId
    || filters.applicationBatchId || filters.sessionId,
  );

  const { data, isLoading } = useQuery({
    queryKey: ['get-student-users', hasActiveSelectFilter ? 'filtered' : currentPage, { ...filters, name: debouncedName }],
    queryFn: () => getAllStudentUser({
      PageNumber: hasActiveSelectFilter ? 1 : currentPage,
      PageSize: hasActiveSelectFilter ? 2000 : 10,
      programId: filters.programId, modeOfStudyId: filters.modeOfStudyId,
      programTypeId: filters.programTypeId, applicationBatchId: filters.applicationBatchId,
      sessionId: filters.sessionId,
      name: debouncedName || undefined,
    }),
  });

  const [programQuery, modeOfStudyQuery, programTypeQuery, batchQuery, sessionQuery] = useQueries({
    queries: [
      { queryKey: ['programs'], queryFn: () => getAllPrograms() },
      { queryKey: ['mode-of-study'], queryFn: getAllModeOfStudy },
      { queryKey: ['program-types'], queryFn: getAllProgramType },
      { queryKey: ['application-batches'], queryFn: getAllApplicationBatch },
      { queryKey: ['sessions'], queryFn: () => getAllAcademicSession() },
    ],
  });

  // ── multilevel ────────────────────────────────────────────────────────────────
  const { schoolConfigData } = useGetSchoolConfigQuery();
  const isMultilevelEnabled = schoolConfigData?.enableMultilevelAdmissionApproval ?? false;

  const { data: stagesData } = useQuery({
    queryKey: ['get-admission-stages'],
    queryFn: getAdmissionStages,
    enabled: isMultilevelEnabled,
  });

  // ALL stages whose requiredRole list includes any of the current user's roles
  const userStages = useMemo<AdmissionStage[]>(() => {
    if (!isMultilevelEnabled || !stagesData?.data?.length) return [];
    return stagesData.data.filter((stage) =>
      stage.requiredRole
        .split(',')
        .map((r) => r.trim().toLowerCase())
        .some((r) => userRoles.map((ur) => ur.toLowerCase()).includes(r)),
    );
  }, [stagesData, userRoles, isMultilevelEnabled]);

  // Pre-fetch pending counts for each matched stage (pageSize=1 is lightweight).
  // Same query key as PendingApprovalTab so React Query shares the cache.
  const pendingCountQueries = useQueries({
    queries: userStages.map((stage) => ({
      queryKey: ['pending-approvals', stage.stageOrder, 1],
      queryFn: () => getPendingApprovals({ stageOrder: stage.stageOrder, page: 1, pageSize: 1 }),
      enabled: isMultilevelEnabled && userStages.length > 0,
    })),
  });

  // Only hide the very first stage when it has no pending items
  // (direct admit/reject in "All Applicants" bypasses stage 1).
  // All other stages always show for users with matching roles.
  const minStageOrder = userStages.length
    ? Math.min(...userStages.map((s) => s.stageOrder))
    : 0;

  const stagesWithPending = userStages.filter((stage, i) => {
    if (stage.stageOrder !== minStageOrder) return true; // non-first stages always visible
    const q = pendingCountQueries[i];
    if (!q || q.isLoading) return false; // hide first stage while its count is loading
    return (q.data?.totalRecords ?? 0) > 0;
  });

  const showApprovalTabs = isMultilevelEnabled && stagesWithPending.length > 0;

  // ── existing data / options ───────────────────────────────────────────────────
  const programData = programQuery?.data?.data as Program[];
  const modeOfStudyData = modeOfStudyQuery?.data?.data as ModeOfStudy[];
  const programTypeData = programTypeQuery?.data?.data as ProgramType[];
  const batchData = batchQuery?.data?.data as ApplicationBatch[];
  // Applicants don't carry a sessionId directly — it's derived from their batch.
  const batchSessionMap = useMemo(
    () => new Map((batchData ?? []).map((b) => [b.id, b.sessionId])),
    [batchData],
  );
  const sessionData = sessionQuery?.data?.data as Session[];

  const programOptions = programData?.map((p) => ({ label: p.name, value: p.id }));

  const { data: programDetailsData } = useQuery({
    queryKey: ['program-details-change-course'],
    queryFn: () => getAllProgram(),
  });
  const changeCourseProgOptions = (programDetailsData?.data ?? []).map((item) => ({
    label: `${item.programName}${item.programCode ? ` (${item.programCode})` : ''}`,
    value: item.id,
  }));
  const modeOfStudyOptions = modeOfStudyData?.map((m) => ({ label: m.name, value: m.id }));
  const programTypeOptions = programTypeData?.map((pt) => ({ label: pt.name, value: pt.id }));
  const batchOptions = batchData?.map((b) => ({ label: b.batchName, value: b.id }));
  const sessionOptions = sessionData?.map((s) => ({ label: s.name, value: s.id }));

  const { specializationData, isSpecializationLoading } = useGetSpecializationQuery();

  const handleNameSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, name: e.target.value }));
    onChange(1, 10);
  };

  const handleAppNumberSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAppNumberSearch(e.target.value);
    onChange(1, 10);
  };

  // Resets to page 1 whenever a dropdown filter changes so the user doesn't land
  // on a now-out-of-range page with a stale/empty result set.
  const updateFilter = (key: keyof ApplicantParams) => (value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    onChange(1, 10);
  };

  const handleChangeCourse = async (
    values: { newProgramId: number; newConcentrationId: number | null; reason: string },
    resetForm: () => void,
  ) => {
    if (!changeCourseApplicant) return;
    try {
      await changeCourseMutation.mutateAsync(
        {
          applicantId: changeCourseApplicant.applicantId,
          newProgramId: values.newProgramId,
          newConcentrationId: values.newConcentrationId,
          reason: values.reason.trim(),
        },
        {
          onSuccess: (res) => {
            notification.success({
              message: 'Success',
              description: res?.message || 'Course changed successfully',
            });
            queryClient.invalidateQueries({ queryKey: ['get-student-users'] });
            setChangeCourseApplicant(null);
            resetForm();
          },
        },
      );
    } catch (err: any) {
      notification.error({
        message: 'Error',
        description: err?.response?.data?.message || 'Failed to change course',
      });
    }
  };

  const viewApplicantDetails = (record: User) =>
    navigate(`/applicants/${record.applicantId}`, { state: { applicationDetails: record } });

  const viewCourseChangeAudit = (record: User) =>
    navigate(`/applicants/${record.applicantId}/course-change-audit`, { state: { applicant: record } });

  const isRejectedApplicant = (record: User) =>
    String(record?.isAdmitted ?? '').toLowerCase() === 'rejected';

  // Only fully-admitted rows are greyed out; rejected rows stay selectable for undo
  const isAdmittedRow = (record: User) =>
    String(record?.isAdmitted ?? '').toLowerCase() === 'accept';

  const handleDownloadApplicants = async () => {
    await downloadApplicantsMutation.mutateAsync(
      { ...filters, name: debouncedName || undefined },
      { onSuccess: (d) => downloadExcelFile(d as Blob, 'Applicant_Report.xlsx') },
    );
  };

  const handleAdmitApplicants = async () => {
    await admitApplicantsMutation.mutateAsync(
      { applicationNumber: selectedApplicants as string[] },
      {
        onSuccess: (d) => {
          notification.success({ message: 'Success', description: d?.message || 'Applicants admitted successfully' });
          setSelectedApplicants([]);
        },
      },
    );
  };

  const handleRejectApplicants = async (rejectionReason: string, resetForm: () => void) => {
    try {
      await rejectApplicantMutation.mutateAsync(
        { applicationNumbers: selectedApplicants as string[], reason: rejectionReason },
        {
          onSuccess: (res) => {
            notification.success({ message: 'Success', description: res?.message || `${selectedApplicants.length} applicant(s) rejected successfully` });
            queryClient.invalidateQueries({ queryKey: ['get-student-users'] });
            setSelectedApplicants([]);
            setIsRejectModalOpen(false);
            resetForm();
          },
        },
      );
    } catch (error: any) {
      notification.error({ message: 'Error', description: error?.response?.data?.message || 'Failed to reject applicants' });
    }
  };

  const handleBulkUndoRejection = async (reason: string, resetForm: () => void) => {
    const applicantIds = (userData?.filter((u) => selectedApplicants.includes(u.applicationNumber)) ?? []).map((u) => u.applicantId);
    try {
      await bulkUndoMutation.mutateAsync(
        { applicantIds, reason },
        {
          onSuccess: (res) => {
            notification.success({ message: 'Success', description: res?.message || `${applicantIds.length} rejection(s) undone successfully` });
            queryClient.invalidateQueries({ queryKey: ['get-student-users'] });
            setSelectedApplicants([]);
            setIsBulkUndoModalOpen(false);
            resetForm();
          },
        },
      );
    } catch (error: any) {
      notification.error({ message: 'Error', description: error?.response?.data?.message || 'Failed to undo rejections' });
    }
  };

  const rowSelection = {
    selectedRowKeys: selectedApplicants,
    onChange: setSelectedApplicants,
    // Admitted rows are disabled — rejected rows stay selectable for undo
    getCheckboxProps: (record: User) => ({
      disabled: isAdmittedRow(record),
    }),
  };
  const hasSelected = selectedApplicants.length > 0;
  const userData = data?.data as User[];
  const userTotal = data?.totalSize as number;

  // Client-side safety net: re-check every active dropdown filter against the row
  // itself, since the backend doesn't reliably filter by these fields.
  const selectFilteredData = hasActiveSelectFilter
    ? userData?.filter((u) => {
        if (filters.programId && u.programId !== filters.programId) return false;
        if (filters.modeOfStudyId && u.modeOfStudyId !== filters.modeOfStudyId) return false;
        if (filters.programTypeId && u.programTypeId !== filters.programTypeId) return false;
        if (filters.applicationBatchId && u.applicationBatchId !== filters.applicationBatchId) return false;
        if (filters.sessionId && batchSessionMap.get(u.applicationBatchId) !== filters.sessionId) return false;
        return true;
      })
    : userData;

  const filteredData = debouncedAppNumber
    ? selectFilteredData?.filter((u) =>
        u.applicationNumber.toLowerCase().includes(debouncedAppNumber.toLowerCase()),
      )
    : selectFilteredData;

  const columns: ColumnsType<User> = [
    { key: 'appNo', title: 'Application No', dataIndex: 'applicationNumber' },
    { key: 'firstName', title: 'First Name', dataIndex: 'firstName' },
    { key: 'lastName', title: 'Last Name', dataIndex: 'lastName' },
    { key: 'programName', title: 'Program Name', dataIndex: 'programName' },
    { key: 'programTypeName', title: 'Program Type', dataIndex: 'programTypeName' },
    { key: 'applicationBatchName', title: 'Application Batch', dataIndex: 'applicationBatchName' },
    { key: 'modeofStudyName', title: 'Mode of Study', dataIndex: 'modeofStudyName' },
    {
      key: 'isAdmitted', title: 'Admitted', dataIndex: 'isAdmitted',
      render: (val: string | null) => {
        if (!val || val.toLowerCase() === 'null') return <Tag color="default">Pending</Tag>;
        if (val.toLowerCase() === 'accept') return <Tag color="success">Yes</Tag>;
        if (val.toLowerCase() === 'reject' || val.toLowerCase() === 'rejected') return <Tag color="error">Rejected</Tag>;
        return <Tag color="processing">{val}</Tag>;
      },
    },
    { key: 'email', title: 'Email', dataIndex: 'email' },
    { key: 'phoneNo', title: 'Phone Number', dataIndex: 'phoneNumber' },
    {
      key: 'action', title: 'Action',
      render: (_, record) => {
        const items = [
          { label: 'View Details', key: 'view-details', onClick: () => viewApplicantDetails(record) },
          { label: 'Change Course', key: 'change-course', onClick: () => setChangeCourseApplicant(record) },
          { label: 'Course Change Audit', key: 'course-change-audit', onClick: () => viewCourseChangeAudit(record) },
          isRejectedApplicant(record)
            ? { label: 'Undo Rejection', key: 'undo-rejection', onClick: () => setSelectedResetApplicant(record) }
            : null,
        ].filter(Boolean);
        return <Dropdown menu={{ items }} trigger={['click']}><Ellipsis /></Dropdown>;
      },
    },
  ];

  const allApplicantsCard = (
    <Card bordered={false}>
      <div className={styles.filters}>
        <div>
          <h5>Filters</h5>
          <Flex gap="8px" wrap="wrap">
            <Select placeholder="Program" allowClear value={filters.programId || undefined} options={programOptions} loading={programQuery.isLoading} onChange={updateFilter('programId')} />
            <Select placeholder="Mode of Study" allowClear value={filters.modeOfStudyId || undefined} options={modeOfStudyOptions} loading={modeOfStudyQuery.isLoading} onChange={updateFilter('modeOfStudyId')} />
            <Select placeholder="Degree Type" allowClear value={filters.programTypeId || undefined} options={programTypeOptions} loading={programTypeQuery.isLoading} onChange={updateFilter('programTypeId')} />
            <Select placeholder="Application Batch" allowClear value={filters.applicationBatchId || undefined} options={batchOptions} loading={batchQuery.isLoading} onChange={updateFilter('applicationBatchId')} />
            <Select placeholder="Session" allowClear value={filters.sessionId || undefined} options={sessionOptions} loading={sessionQuery.isLoading} onChange={updateFilter('sessionId')} />
          </Flex>
        </div>
        <Flex gap="8px" wrap="wrap">
          <Input prefix={<Search />} placeholder="Search by name" value={filters.name} onChange={handleNameSearch} allowClear className={styles.input} />
          <Input prefix={<Search />} placeholder="Search by application number" value={appNumberSearch} onChange={handleAppNumberSearch} allowClear className={styles.input} />
        </Flex>
      </div>
      <br />
      {hasSelected && (
        <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
          <Flex gap="8px">
            <Button text="Admit Selected Applicants" onClick={handleAdmitApplicants} isLoading={admitApplicantsMutation.isPending} disabled={admitApplicantsMutation.isPending || rejectApplicantMutation.isPending || bulkUndoMutation.isPending} />
            <Button text="Deny Admission" onClick={() => setIsRejectModalOpen(true)} disabled={admitApplicantsMutation.isPending || rejectApplicantMutation.isPending || bulkUndoMutation.isPending} style={{ backgroundColor: '#dc2626', color: '#fff' }} />
          </Flex>
          <Button text="Undo Denied Applicant" onClick={() => setIsBulkUndoModalOpen(true)} isLoading={bulkUndoMutation.isPending} disabled={admitApplicantsMutation.isPending || rejectApplicantMutation.isPending || bulkUndoMutation.isPending} style={{ backgroundColor: 'green', color: '#fff' }} />
        </Flex>
      )}
      <br />
      <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
        <p style={{ margin: 0 }}>
          Showing {filteredData?.length ?? 0}
          {debouncedAppNumber
            ? ` (filtered from ${selectFilteredData?.length ?? 0})`
            : hasActiveSelectFilter
            ? ` matching selected filters`
            : ` of ${userTotal}`}
        </p>
        <p style={{ margin: 0, fontSize: 12, color: '#98a2b3' }}>
          Greyed = admitted &nbsp;|&nbsp; <span style={{ color: '#ef4444' }}>Red = rejected (select to undo)</span>
        </p>
      </Flex>
      <Table
        dataSource={filteredData} columns={columns}
        rowKey={(r) => r.applicationNumber} rowSelection={rowSelection}
        scroll={{ x: true }} loading={isLoading}
        onRow={(record) => ({
          style: isAdmittedRow(record)
            ? { opacity: 0.4, background: '#f9fafb', cursor: 'default' }
            : isRejectedApplicant(record)
            ? { background: '#fff1f0' }
            : {},
        })}
        pagination={{
          position: ['bottomCenter'],
          current: currentPage,
          total: hasActiveSelectFilter ? (filteredData?.length ?? 0) : userTotal,
          onChange,
          pageSize: 10,
        }}
      />
    </Card>
  );

  // Build tab items: "All Applicants" + one tab per matched stage (sorted by stageOrder)
  const tabItems = useMemo(() => {
    const base = [{ key: 'all', label: 'All Applicants', children: allApplicantsCard }];
    const stageTabs = [...stagesWithPending]
      .sort((a, b) => a.stageOrder - b.stageOrder)
      .map((stage) => ({
        key: `stage-${stage.stageOrder}`,
        label: (
          <span>
            Stage {stage.stageOrder} — {stage.stageName}
            {stage.isFinalStage && <Tag color="green" style={{ marginLeft: 4, fontSize: 11 }}>Final</Tag>}
          </span>
        ),
        children: <PendingApprovalTab stage={stage} />,
      }));
    return [...base, ...stageTabs];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStages, allApplicantsCard]);

  return (
    <div>
      <section className="space-between">
        <h3>Applicants Admission page</h3>
        <Button onClick={handleDownloadApplicants} text="Download Report" isLoading={downloadApplicantsMutation.isPending} />
      </section>

      <br />

      {showApprovalTabs ? (
        <Tabs defaultActiveKey="all" items={tabItems} />
      ) : (
        allApplicantsCard
      )}

      <Modal open={isBulkUndoModalOpen} onCancel={() => setIsBulkUndoModalOpen(false)} footer={null} destroyOnClose title="Undo Rejection">
        <Formik
          initialValues={{ reason: '' }}
          validationSchema={Yup.object().shape({ reason: Yup.string().trim().required('Reason is required') })}
          onSubmit={async (values, { resetForm }) => handleBulkUndoRejection(values.reason.trim(), resetForm)}
        >
          <Form className={styles.verifyForm}>
            <p style={{ marginBottom: 8, color: '#dc2626', fontSize: 13 }}>You are about to undo rejection for <strong>{selectedApplicants.length}</strong> applicant(s).</p>
            <TextInput name="reason" label="Reason" placeholder="Enter reason for undoing rejection" type="textarea" />
            <Flex justify="flex-end" gap="12px" className={styles.verifyActions}>
              <Button type="button" variant="text" text="Cancel" disabled={bulkUndoMutation.isPending} onClick={() => setIsBulkUndoModalOpen(false)} />
              <Button type="submit" text={bulkUndoMutation.isPending ? 'Processing...' : 'Undo Rejection'} isLoading={bulkUndoMutation.isPending} disabled={bulkUndoMutation.isPending} style={{ backgroundColor: 'green', color: '#fff' }} />
            </Flex>
          </Form>
        </Formik>
      </Modal>
      <Modal open={isRejectModalOpen} onCancel={() => setIsRejectModalOpen(false)} footer={null} destroyOnClose title="Reject Applicants">
        <Formik
          initialValues={{ rejectionReason: '' }}
          validationSchema={Yup.object().shape({ rejectionReason: Yup.string().trim().required('Rejection reason is required') })}
          onSubmit={async (values, { resetForm }) => handleRejectApplicants(values.rejectionReason.trim(), resetForm)}
        >
          <Form className={styles.verifyForm}>
            <p style={{ marginBottom: 8, color: '#dc2626', fontSize: 13 }}>You are about to reject <strong>{selectedApplicants.length}</strong> applicant(s). This action can be undone per applicant from the action menu.</p>
            <TextInput name="rejectionReason" label="Rejection Reason" placeholder="Enter rejection reason" type="textarea" />
            <Flex justify="flex-end" gap="12px" className={styles.verifyActions}>
              <Button type="button" variant="text" text="Cancel" disabled={rejectApplicantMutation.isPending} onClick={() => setIsRejectModalOpen(false)} />
              <Button type="submit" text={rejectApplicantMutation.isPending ? 'Rejecting...' : 'Reject'} isLoading={rejectApplicantMutation.isPending} disabled={rejectApplicantMutation.isPending} style={{ backgroundColor: '#dc2626' }} />
            </Flex>
          </Form>
        </Formik>
      </Modal>

      {/* ── Change Course Modal ─────────────────────────────────────────── */}
      <Modal
        open={Boolean(changeCourseApplicant)}
        onCancel={() => setChangeCourseApplicant(null)}
        footer={null}
        destroyOnClose
        title={`Change Course — ${changeCourseApplicant?.firstName ?? ''} ${changeCourseApplicant?.lastName ?? ''}`}
      >
        {changeCourseApplicant && (
          <Formik
            initialValues={{ newProgramId: 0, newConcentrationId: null as number | null, reason: '' }}
            validationSchema={Yup.object().shape({
              newProgramId: Yup.number().min(1, 'Program is required').required('Program is required'),
              reason: Yup.string().trim().required('Reason is required'),
            })}
            onSubmit={async (values, { resetForm }) => handleChangeCourse(values, resetForm)}
          >
            {({ values, setFieldValue, errors, touched }) => (
              <Form className={styles.verifyForm}>
                <p style={{ marginBottom: 12, fontSize: 13, color: '#6b7280' }}>
                  Current programme: <strong>{changeCourseApplicant.programName}</strong>
                </p>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>
                    New Programme <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <Select
                    placeholder="Select programme"
                    style={{ width: '100%' }}
                    showSearch
                    optionFilterProp="label"
                    options={changeCourseProgOptions}
                    value={values.newProgramId || undefined}
                    onChange={(v) => {
                      setFieldValue('newProgramId', v);
                      setFieldValue('newConcentrationId', null);
                    }}
                  />
                  {touched.newProgramId && errors.newProgramId && (
                    <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.newProgramId}</p>
                  )}
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>
                    Specialization / Concentration
                  </label>
                  <Select
                    placeholder="Select specialization (optional)"
                    style={{ width: '100%' }}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    loading={isSpecializationLoading}
                    options={specializationData
                      .filter((s) => s.academicProgramId === values.newProgramId)
                      .map((s) => ({ label: s.name, value: s.id }))}
                    value={values.newConcentrationId ?? undefined}
                    onChange={(v) => setFieldValue('newConcentrationId', v ?? null)}
                    disabled={!values.newProgramId}
                  />
                </div>

                <TextInput name="reason" label="Reason" placeholder="Enter reason for course change" type="textarea" />

                <Flex justify="flex-end" gap="12px" className={styles.verifyActions}>
                  <Button
                    type="button"
                    variant="text"
                    text="Cancel"
                    disabled={changeCourseMutation.isPending}
                    onClick={() => setChangeCourseApplicant(null)}
                  />
                  <Button
                    type="submit"
                    text={changeCourseMutation.isPending ? 'Saving...' : 'Change Course'}
                    isLoading={changeCourseMutation.isPending}
                    disabled={changeCourseMutation.isPending}
                  />
                </Flex>
              </Form>
            )}
          </Formik>
        )}
      </Modal>

      <Modal open={Boolean(selectedResetApplicant)} onCancel={() => setSelectedResetApplicant(null)} footer={null} destroyOnClose title="Undo Rejection">
        {selectedResetApplicant && (
          <Formik
            initialValues={{ reason: '' }}
            validationSchema={Yup.object().shape({ reason: Yup.string().trim().required('Reason is required') })}
            onSubmit={async (values, { resetForm }) => resetRejectionHandler({ applicantId: selectedResetApplicant.applicantId, reason: values.reason.trim() }, resetForm)}
          >
            <Form className={styles.verifyForm}>
              <TextInput name="reason" label="Reason" placeholder="Enter reset reason" type="textarea" />
              <Flex justify="flex-end" gap="12px" className={styles.verifyActions}>
                <Button type="button" variant="text" text="Cancel" disabled={isResetRejectionLoading} onClick={() => setSelectedResetApplicant(null)} />
                <Button type="submit" text={isResetRejectionLoading ? 'Submitting...' : 'Submit'} isLoading={isResetRejectionLoading} disabled={isResetRejectionLoading} />
              </Flex>
            </Form>
          </Formik>
        )}
      </Modal>
    </div>
  );
};

export default StudentUser;
